import { useCallback } from 'react';

import { useQueryClient } from '@eika-infrastruktur/react-query';

import { ensureAccountsData, useAccounts } from '../../../../fetchHooks/useAccount';
import type { Account } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { isValidAvtalegiroFromAccount } from '../../../../services/accountFilters';
import TranslationsConfigType from '../../../../translations/translationType';
import { UseValidateHook } from '../../../../utils/validation/validateTypes';
import { validKid } from '../../Payment/form/kidService';
import { SelectedReceiver } from '../../SelectedReceiver';
import { AvtaleGiroState } from './state';

const MAX_AMOUNT_LIMIT = 9999999;
export const MAX_NOTE_LIMIT = 30;

export interface AvtaleGiroError {
    accountId?: string;
    toAccount?: string;
    kid?: string;
    note?: string;
    transactionLimit?: string;
    monthlyLimit?: string;
}

export const validateKid = (
    translated: TranslationsConfigType,
    kid?: string,
    toAccount?: SelectedReceiver,
): string | undefined => {
    if (!kid) {
        return translated.avtalegiroValidation.emptyKID;
    }
    if (!toAccount) {
        return translated.avtalegiroValidation.missingReceiver;
    }
    if (!validKid(toAccount?.kidFormats, kid)) {
        return translated.paymentValidation.invalidKID;
    }
    return undefined;
};

export const validateReceiver = (
    translated: TranslationsConfigType,
    toAccount?: SelectedReceiver,
): string | undefined => {
    if (!toAccount) {
        return translated.paymentValidation.chooseToAccount;
    }
    if (!toAccount.avtaleGiroSupported) {
        return translated.avtalegiroValidation.invalidToAccount;
    }
    return undefined;
};

export const validateAmountLimit = (
    translated: TranslationsConfigType,
    transactionLimit: number,
    monthlyLimit: number,
) => {
    const errorObject: { transactionLimit?: string; monthlyLimit?: string } = {
        transactionLimit: undefined,
        monthlyLimit: undefined,
    };
    if (transactionLimit > MAX_AMOUNT_LIMIT) {
        errorObject.transactionLimit = format(translated.avtalegiroValidation.amountTooHigh, MAX_AMOUNT_LIMIT);
    }
    if (monthlyLimit > MAX_AMOUNT_LIMIT) {
        errorObject.monthlyLimit = format(translated.avtalegiroValidation.amountTooHigh, MAX_AMOUNT_LIMIT);
    }

    if (transactionLimit === 0) {
        errorObject.transactionLimit = translated.avtalegiroValidation.transactionLimitIsZero;
    }
    if (monthlyLimit === 0) {
        errorObject.monthlyLimit = translated.avtalegiroValidation.monthlyLimitIsZero;
    }

    if (transactionLimit > monthlyLimit) {
        errorObject.monthlyLimit = translated.avtalegiroValidation.monthlyMaxAmountMustBeLargerThanTransactional;
        errorObject.transactionLimit = translated.avtalegiroValidation.transactionLimitMustBeLessThanMonthly;
    }
    return errorObject;
};

export const validate =
    (translated: TranslationsConfigType, accounts: Account[]) =>
    (formState: AvtaleGiroState): AvtaleGiroError => {
        const errors: AvtaleGiroError = {
            accountId: undefined,
            toAccount: undefined,
            kid: undefined,
            note: undefined,
            transactionLimit: undefined,
            monthlyLimit: undefined,
        };

        const { fromAccount, toAccount, kid, transactionLimit, monthlyLimit, note } = formState;
        const targetFromAccount = accounts
            .filter(isValidAvtalegiroFromAccount)
            .find((account) => account.accountNumber.value === fromAccount?.value);

        if (!fromAccount || !targetFromAccount?.accountId || targetFromAccount?.accountId.value === '') {
            errors.accountId = translated.paymentValidation.chooseFromAccount;
        }

        if (note && note.length > MAX_NOTE_LIMIT) {
            errors.note = format(translated.tooManyCharacters, MAX_NOTE_LIMIT);
        }

        errors.toAccount = validateReceiver(translated, toAccount);
        errors.kid = validateKid(translated, kid?.value, toAccount);
        const limitValidationMessages = validateAmountLimit(translated, transactionLimit, monthlyLimit);
        errors.monthlyLimit = limitValidationMessages.monthlyLimit;
        errors.transactionLimit = limitValidationMessages.transactionLimit;

        return errors;
    };

export const useValidate: UseValidateHook<AvtaleGiroState, AvtaleGiroError> = () => {
    const translated = useTranslation();
    const queryClient = useQueryClient();
    const { data: accounts } = useAccounts();
    return useCallback(
        async (state) => {
            const currentAccounts = await ensureAccountsData(queryClient);
            return validate(translated, accounts ?? currentAccounts)(state);
        },
        [translated, accounts, queryClient],
    );
};

export default useValidate;
